import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  useTheme,
  alpha,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Switch,
  TablePagination,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Assignment as AssignmentIcon,
  Science as ScienceIcon,
  AttachMoney as CostIcon,
  Verified as VerifiedIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Analytics as AnalyticsIcon,
  LocalDrink as LocalDrinkIcon,
  Factory as FactoryIcon,
  Print as PrintIcon,
  Summarize as SummarizeIcon,
  Info as InfoIcon,
  Speed as SpeedIcon,
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  Timer as TimerIcon,
  Category as CategoryIcon,
  Code as CodeIcon,
  SupervisorAccount as SupervisorIcon,
  LocalShipping as LocalShippingIcon,
  Engineering as EngineeringIcon,
  Widgets as WidgetsIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import { useNavigate } from 'react-router-dom';
import { convertUnits, getUnitType } from '../../../utils/unitConversions';
import { usageCapConfig } from '../../inventory/inventory-components/Usage/usageConfigs/usageCapConfig';
import { usageBottleConfig } from '../../inventory/inventory-components/Usage/usageConfigs/usageBottleConfig';
import { usageLabelConfig } from '../../inventory/inventory-components/Usage/usageConfigs/usageLabelConfig';
import { usageSupplyConfig } from '../../inventory/inventory-components/Usage/usageConfigs/usageSupplyConfig';
import { submitAddStockProduct } from '../../inventory/inventory-components/Add/addConfigs/addProductConfig';
import dayjs from 'dayjs';

export default function ProductionLineSummary({ lineProduction, onPrint, onExport, onUpdate, showSnackbar, user }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [lineTimeFormat, setLineTimeFormat] = useState('12hour');
  const [summaryData, setSummaryData] = useState({
    lineCosts: null,
    lineLog: null,
    qcTests: [],
    signatures: [],
    product: null,
    template: null,
    templateConsumption: {
      caps: [],
      bottles: [],
      labels: [],
      supplies: []
    },
    location: null,
    supplies: [],
    yieldData: null,
    productVariant: null,
    unitConfiguration: null
  });
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalSignature, setApprovalSignature] = useState('');
  const [signaturePreviewName, setSignaturePreviewName] = useState('');
  const [unapproveDialogOpen, setUnapproveDialogOpen] = useState(false);
  const [unapproving, setUnapproving] = useState(false);
  const [unapprovalSignature, setUnapprovalSignature] = useState('');
  const [unapprovalReason, setUnapprovalReason] = useState('');
  const [unapprovalSignaturePreview, setUnapprovalSignaturePreview] = useState('');
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [approvalPage, setApprovalPage] = useState(0);
  const [approvalRowsPerPage, setApprovalRowsPerPage] = useState(5);
  const [inventoryCheckLoading, setInventoryCheckLoading] = useState(false);
  const [hasEnoughInventory, setHasEnoughInventory] = useState(true);
  const [insufficientInventoryItems, setInsufficientInventoryItems] = useState([]);
  const [shouldCreateUsageTransactions, setShouldCreateUsageTransactions] = useState(true);
  const [shouldCreateInventoryAdjustment, setShouldCreateInventoryAdjustment] = useState(true);
  const [selectedInventoryLocation, setSelectedInventoryLocation] = useState(null);
  const [inventoryLocationDialogOpen, setInventoryLocationDialogOpen] = useState(false);
  const [inventoryLocationSearchTerm, setInventoryLocationSearchTerm] = useState('');
  const [dialogSelectedInventoryLocation, setDialogSelectedInventoryLocation] = useState(null);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [allStepsCompleted, setAllStepsCompleted] = useState(false);
  const [stepCompletionStatus, setStepCompletionStatus] = useState({
    overview: false,
    items: false,
    costs: false,
    log: false
  });
  const [accordionState, setAccordionState] = useState({
    productDetails: false,
    schedule: false,
    unitConfig: false,
    batchInfo: false,
    supplies: false,
    approvalHistory: false
  });

  // Load accordion states from localStorage on mount
  useEffect(() => {
    const savedStates = localStorage.getItem(`lineProductionSummary_accordions_${lineProduction?.id || 'default'}`);
    if (savedStates) {
      try {
        const parsedStates = JSON.parse(savedStates);
        setAccordionState(parsedStates);
      } catch (error) {
        console.warn('Failed to parse saved accordion states:', error);
      }
    }
  }, [lineProduction?.id]);

  // Save accordion states to localStorage when they change
  const updateAccordionState = (accordionKey, expanded) => {
    const newStates = { ...accordionState, [accordionKey]: expanded };
    setAccordionState(newStates);
    localStorage.setItem(`lineProductionSummary_accordions_${lineProduction?.id || 'default'}`, JSON.stringify(newStates));
  };

  // Suppress ResizeObserver errors globally - Fix for accordion intersection issues
  useEffect(() => {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (args[0] && args[0].toString().includes('ResizeObserver loop completed with undelivered notifications')) {
        return; // Suppress this specific error
      }
      originalConsoleError.apply(console, args);
    };
    
    // Also handle as window error event
    const handleResizeObserverError = (e) => {
      if (e.message && e.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        e.preventDefault();
        e.stopPropagation();
        return true;
      }
    };
    
    window.addEventListener('error', handleResizeObserverError);
    
    return () => {
      // Restore original console.error
      console.error = originalConsoleError;
      window.removeEventListener('error', handleResizeObserverError);
    };
  }, []);

  // Load user profile for time format preference
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        if (!user?.id) return;
        
        const { data, error } = await supabase
          .from('profiles')
          .select('line_production_time_format_preference')
          .eq('id', user.id)
          .single();
          
        if (data && !error) {
          setLineTimeFormat(data.line_production_time_format_preference || '12hour');
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
        setLineTimeFormat('12hour');
      }
    };
    
    loadUserProfile();
  }, [user?.id]);

  useEffect(() => {
    if (lineProduction?.id && typeof lineProduction.id === 'string') {
      loadSummaryData();
      fetchApprovalHistory();
      checkStepsCompletion();
      loadAvailableLocations();
    }
  }, [lineProduction]);

  // Load available locations for inventory
  const loadAvailableLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('id, name, city, state, is_main')
        .eq('is_active', true)
        .order('is_main', { ascending: false })
        .order('name');
      
      if (error) throw error;
      
      // Sort so main location is first, then alphabetically
      const sortedLocations = (data || []).sort((a, b) => {
        if (a.is_main && !b.is_main) return -1;
        if (!a.is_main && b.is_main) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setAvailableLocations(sortedLocations);
      
      // Set default to main location if available, otherwise production location
      if (!selectedInventoryLocation) {
        const mainLocation = sortedLocations.find(loc => loc.is_main);
        if (mainLocation) {
          setSelectedInventoryLocation(mainLocation.id);
        } else if (lineProduction?.production_location) {
          setSelectedInventoryLocation(lineProduction.production_location);
        }
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      setAvailableLocations([]);
    }
  };
  
  const checkStepsCompletion = async () => {
    const completed = await checkAllStepsCompleted();
    setAllStepsCompleted(completed);
  };
  
  // Recheck completion status when summary data changes
  useEffect(() => {
    if (lineProduction?.id && summaryData) {
      checkStepsCompletion();
    }
  }, [summaryData, lineProduction?.id]);

  const loadSummaryData = async () => {
    if (!lineProduction?.id) return;

    // Ensure lineProduction.id is a string
    const lineProductionId = String(lineProduction.id);
    
    setLoading(true);
    try {
      // First get line log to get line_log_id for linking related tables
      const { data: lineLogData, error: lineLogError } = await supabase
        .from('production_line_logs')
        .select('*')
        .eq('line_production_id', lineProductionId)
        .single();

      const lineLogId = lineLogData?.id;

      // Load all data in parallel
      const [
        costsResult,
        qcResult,
        signaturesResult,
        productResult,
        templateResult,
        templateCapsResult,
        templateBottlesResult,
        templateLabelsResult,
        templateSuppliesResult,
        locationResult,
        suppliesResult,
        yieldResult,
        productVariantResult,
        unitConfigResult
      ] = await Promise.all([

        // Line costs
        supabase
          .from('production_line_costs')
          .select('*')
          .eq('line_production_id', lineProductionId)
          .single(),

        // QC tests from production_line_quality_control table
        supabase
          .from('production_line_quality_control')
          .select('*')
          .eq('line_production_id', lineProductionId)
          .order('created_at', { ascending: false }),

        // Signatures
        supabase
          .from('production_line_signatures')
          .select('*')
          .eq('line_production_id', lineProductionId),

        // Product details
        lineProduction?.product_id ? supabase.from('products').select('*').eq('id', lineProduction.product_id).single() : Promise.resolve({ data: null, error: null }),
        
        // Template details
        lineProduction?.template_id ? supabase.from('production_line_templates').select('*').eq('id', lineProduction.template_id).single() : Promise.resolve({ data: null, error: null }),
        
        // Template caps consumption
        lineProduction?.template_id ? supabase
          .from('template_caps_consumption')
          .select(`
            *,
            caps:cap_id (
              id, name, sku, color, closure_type, material
            )
          `)
          .eq('template_id', lineProduction.template_id) : Promise.resolve({ data: [], error: null }),

        // Template bottles consumption
        lineProduction?.template_id ? supabase
          .from('template_bottles_consumption')
          .select(`
            *,
            bottles:bottle_id (
              id, name, sku, capacity, capacity_unit, color, material
            )
          `)
          .eq('template_id', lineProduction.template_id) : Promise.resolve({ data: [], error: null }),

        // Template labels consumption
        lineProduction?.template_id ? supabase
          .from('template_labels_consumption')
          .select(`
            *,
            labels:label_id (
              id, name, sku, material, adhesive_type, width_mm, height_mm
            )
          `)
          .eq('template_id', lineProduction.template_id) : Promise.resolve({ data: [], error: null }),

        // Template supplies consumption
        lineProduction?.template_id ? supabase
          .from('template_supplies_consumption')
          .select(`
            *,
            supplies:supply_id (
              id, name, sku, unit, unit_cost
            )
          `)
          .eq('template_id', lineProduction.template_id) : Promise.resolve({ data: [], error: null }),
        
        // Location details
        lineProduction?.production_location ? supabase.from('locations').select('*').eq('id', lineProduction.production_location).single() : Promise.resolve({ data: null, error: null }),
        
        // Supply logs
        lineLogId ? supabase
          .from('production_line_log_supplies')
          .select('*')
          .eq('line_log_id', lineLogId)
          .order('created_at') : Promise.resolve({ data: [], error: null }),

        // Yield data for production timing
        lineLogId ? supabase
          .from('production_line_log_yield')
          .select('*')
          .eq('line_log_id', lineLogId)
          .single() : Promise.resolve({ data: null, error: null }),

        // Product variant details
        lineProduction?.product_variant_id ? supabase
          .from('product_variants')
          .select('*')
          .eq('id', lineProduction.product_variant_id)
          .single() : Promise.resolve({ data: null, error: null }),

        // Unit configurations  
        lineProduction?.unit_configuration_id ? supabase
          .from('unit_configurations')
          .select('*')
          .eq('id', lineProduction.unit_configuration_id)
          .single() : Promise.resolve({ data: null, error: null })
      ]);

      // Load supply lots and attach them to supplies
      let suppliesWithLots = suppliesResult.data || [];
      if (suppliesWithLots.length > 0 && lineLogId) {
        try {
          const { data: supplyLotsData, error: supplyLotsError } = await supabase
            .from('production_line_log_supply_lots')
            .select('*')
            .in('line_supply_log_id', suppliesWithLots.map(s => s.id));

          if (!supplyLotsError && supplyLotsData) {
            // Group lots by supply ID
            const lotsBySupplyId = {};
            supplyLotsData.forEach(lot => {
              if (!lotsBySupplyId[lot.line_supply_log_id]) {
                lotsBySupplyId[lot.line_supply_log_id] = [];
              }
              lotsBySupplyId[lot.line_supply_log_id].push(lot);
            });

            // Attach lots to supplies based on item_type
            suppliesWithLots = suppliesWithLots.map(supply => {
              const lots = lotsBySupplyId[supply.id] || [];
              const supplyWithLots = { ...supply };
              
              // Attach lots based on item_type
              if (supply.item_type === 'bottle' || supply.item_type === 'bottles') {
                supplyWithLots.bottle_lots = lots;
              } else if (supply.item_type === 'cap' || supply.item_type === 'caps') {
                supplyWithLots.cap_lots = lots;
              } else if (supply.item_type === 'label' || supply.item_type === 'labels') {
                supplyWithLots.label_lots = lots;
              } else if (supply.item_type === 'supply' || supply.item_type === 'supplies') {
                supplyWithLots.supply_lots = lots;
              }
              
              return supplyWithLots;
            });
          }
        } catch (error) {
          console.error('Error loading supply lots:', error);
        }
      }

      setSummaryData({
        lineCosts: costsResult.data,
        lineLog: lineLogData,
        qcTests: qcResult.data || [],
        signatures: signaturesResult.data || [],
        product: productResult.data,
        template: templateResult.data,
        templateConsumption: {
          caps: templateCapsResult.data || [],
          bottles: templateBottlesResult.data || [],
          labels: templateLabelsResult.data || [],
          supplies: templateSuppliesResult.data || []
        },
        location: locationResult.data,
        supplies: suppliesWithLots,
        yieldData: yieldResult.data,
        productVariant: productVariantResult.data,
        unitConfiguration: unitConfigResult.data
      });
      
    } catch (error) {
      console.error('Error loading summary data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null || amount === undefined) return '$0.00';
    return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  const formatPercentage = (value) => `${(value || 0).toFixed(1)}%`;
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      if (lineTimeFormat === '24hour') {
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      } else {
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      }
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Invalid date';
    }
  };

  const fetchApprovalHistory = async () => {
    if (!lineProduction?.id) return;
    
    try {
      const { data: historyData, error: historyError } = await supabase
        .from('line_production_approval_history')
        .select('*')
        .eq('line_production_id', lineProduction.id)
        .order('performed_at', { ascending: false });
        
      if (historyError) {
        console.error('Error fetching approval history:', historyError);
        return;
      }
      
      if (!historyData || historyData.length === 0) {
        setApprovalHistory([]);
        return;
      }
      
      const userIds = [...new Set(historyData.map(entry => entry.performed_by).filter(Boolean))];
      
      if (userIds.length === 0) {
        setApprovalHistory(historyData);
        return;
      }
      
      const profilePromises = userIds.map(userId => 
        supabase.rpc('get_profile_by_user_id', { p_user_id: userId })
      );
      
      const profileResults = await Promise.all(profilePromises);
      const profileData = profileResults
        .filter(result => !result.error && result.data && result.data.length > 0)
        .map(result => result.data[0]);
      
      const { data: userEmailData } = await supabase
        .rpc('get_user_emails_for_company', { 
          p_company_id: lineProduction?.company_id 
        });
      
      const enrichedData = historyData.map(entry => {
        const profile = profileData.find(p => p.id === entry.performed_by);
        const userEmail = userEmailData?.find(u => u.user_id === entry.performed_by);
        
        return {
          ...entry,
          profiles: profile || {
            id: entry.performed_by,
            first_name: null,
            last_name: null,
            email: userEmail?.email || null
          }
        };
      });
      
      setApprovalHistory(enrichedData);
    } catch (error) {
      console.error('Error fetching approval history:', error);
      setApprovalHistory([]);
    }
  };

  const checkAllStepsCompleted = async () => {
    if (!lineProduction?.id) return false;
    
    try {
      // 1. Line Overview - lineProduction exists
      const overviewCompleted = !!(lineProduction?.id);
      
      // 2. Line Items - check if items are configured properly
      const { data: items, error: itemsError } = await supabase
        .from('production_line_items')
        .select('id, actual_quantity, item_condition')
        .eq('line_production_id', lineProduction.id);
      
      if (itemsError) throw itemsError;
      
      const itemsCompleted = items && items.length > 0 && items.every(item => 
        item.actual_quantity && item.actual_quantity > 0 && item.item_condition
      );
      
      // 3. Line Costs - check if costs are calculated and completed
      const { data: costs, error: costsError } = await supabase
        .from('production_line_costs')
        .select('is_completed')
        .eq('line_production_id', lineProduction.id)
        .maybeSingle();
      
      if (costsError) throw costsError;
      
      const costsCompleted = costs?.is_completed === true;
      
      // 4. Line Log - check if ALL production log steps are completed
      const { data: logData, error: logError } = await supabase
        .from('production_line_logs')
        .select('overview_completed, area_checklist_completed, supply_log_completed, summary_log_completed, signature_log_completed')
        .eq('line_production_id', lineProduction.id)
        .single();
      
      if (logError && logError.code !== 'PGRST116') {
        console.error('Error loading production line log completion:', logError);
        return false;
      }
      
      const logCompleted = logData ? !!(
        logData.overview_completed && 
        logData.area_checklist_completed && 
        logData.supply_log_completed && 
        logData.summary_log_completed && 
        logData.signature_log_completed
      ) : false;

      const allCompleted = overviewCompleted && itemsCompleted && costsCompleted && logCompleted;
      
      // Update step completion status
      setStepCompletionStatus({
        overview: overviewCompleted,
        items: itemsCompleted,
        costs: costsCompleted,
        log: logCompleted
      });
      
      return allCompleted;
    } catch (error) {
      console.error('Error checking steps completion:', error);
      return false;
    }
  };

  const checkInventoryAvailability = async (supplies) => {
    const insufficientItems = [];

    const configMap = {
      cap: usageCapConfig, caps: usageCapConfig,
      bottle: usageBottleConfig, bottles: usageBottleConfig,
      label: usageLabelConfig, labels: usageLabelConfig,
      supply: usageSupplyConfig, supplies: usageSupplyConfig
    };
    const itemIdMap = {
      cap: 'cap_id', caps: 'cap_id',
      bottle: 'bottle_id', bottles: 'bottle_id',
      label: 'label_id', labels: 'label_id',
      supply: 'supply_id', supplies: 'supply_id'
    };

    for (const supply of (supplies || [])) {
      const actualQuantity = parseFloat(supply.actual_quantity) || 0;
      if (actualQuantity <= 0) continue;

      const config = configMap[supply.item_type];
      const itemId = supply[itemIdMap[supply.item_type]] || supply.item_id;
      if (!config || !itemId || !lineProduction?.company_id) continue;

      try {
        const inventoryItems = await config.getInventoryItems(
          lineProduction.company_id,
          itemId,
          lineProduction.production_location
        );
        const availableQty = inventoryItems.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
        if (availableQty < actualQuantity) {
          insufficientItems.push({
            name: supply.supply_name || 'Unknown',
            needed: actualQuantity,
            available: availableQty,
            type: supply.item_type
          });
        }
      } catch (error) {
        console.error(`Error checking inventory for ${supply.supply_name}:`, error);
      }
    }

    return { hasEnoughInventory: insufficientItems.length === 0, insufficientItems };
  };

  const createInventoryAdjustment = async () => {
    if (!lineProduction?.product_id || !selectedInventoryLocation) {
      return { success: false, error: 'Missing product or location information' };
    }

    try {
      const totalUnitsProduced = summaryData.yieldData?.total_units_produced || lineProduction?.planned_quantity || 0;

      if (totalUnitsProduced <= 0) {
        return { success: false, error: 'No units produced to add to inventory' };
      }

      const mfgDate = lineProduction.created_at
        ? new Date(lineProduction.created_at).toISOString().split('T')[0]
        : null;
      const expiryDate = lineProduction?.expiration_date || null;

      let shelfLife = null;
      if (mfgDate && expiryDate) {
        const mfgD = dayjs(mfgDate);
        const expD = dayjs(expiryDate);
        if (mfgD.isValid() && expD.isValid()) {
          shelfLife = Math.round(expD.diff(mfgD, 'month', true));
        }
      }

      const result = await submitAddStockProduct(lineProduction.company_id, 'products', null, {
        item:        { id: lineProduction.product_id },
        location:    { id: selectedInventoryLocation },
        lotNumber:   lineProduction.lot_number   || null,
        batchNumber: lineProduction.batch_number || null,
        palletQty:   lineProduction.pallet_quantity || 0,
        caseQty:     lineProduction.case_quantity   || 0,
        unitQty:     lineProduction.unit_quantity   || 0,
        mfgDate,
        expiryDate,
        shelfLife,
        variantId:    lineProduction.product_variant_id    || null,
        unitConfigId: lineProduction.unit_configuration_id || null,
        notes:    `Inventory created from line production batch ${lineProduction.batch_number}: ${totalUnitsProduced} units`,
        documents: {}
      });

      if (!result.success) {
        return { success: false, error: result.error };
      }

      return { success: true, unitsAdded: totalUnitsProduced };
    } catch (error) {
      console.error('Error creating inventory adjustment:', error);
      return { success: false, error: error.message };
    }
  };

  const createUsageTransactions = async (supplies) => {
    if (!supplies || supplies.length === 0) return { success: true, errors: [], transactionCount: 0 };

    const errors = [];
    let totalTransactionCount = 0;

    const configMap = {
      cap: usageCapConfig, caps: usageCapConfig,
      bottle: usageBottleConfig, bottles: usageBottleConfig,
      label: usageLabelConfig, labels: usageLabelConfig,
      supply: usageSupplyConfig, supplies: usageSupplyConfig
    };
    const itemIdMap = {
      cap: 'cap_id', caps: 'cap_id',
      bottle: 'bottle_id', bottles: 'bottle_id',
      label: 'label_id', labels: 'label_id',
      supply: 'supply_id', supplies: 'supply_id'
    };
    const lotsKeyMap = {
      cap: 'cap_lots', caps: 'cap_lots',
      bottle: 'bottle_lots', bottles: 'bottle_lots',
      label: 'label_lots', labels: 'label_lots',
      supply: 'supply_lots', supplies: 'supply_lots'
    };

    for (const supply of supplies) {
      const actualQuantity = parseFloat(supply.actual_quantity) || 0;
      const supplyName = supply.supply_name || 'Unknown';

      if (actualQuantity <= 0) continue;

      const config = configMap[supply.item_type];
      const itemId = supply[itemIdMap[supply.item_type]] || supply.item_id;

      if (!config) continue;
      if (!itemId) { errors.push(`${supplyName}: Missing item ID`); continue; }

      const lots = supply[lotsKeyMap[supply.item_type]] || [];

      // Build usages: one entry per lot if lots exist, else one entry for total
      let usages = lots.length > 0
        ? lots
            .map(lot => ({
              itemId,
              lotId: lot.inventory_item_id || null,
              quantity: parseFloat(lot.quantity_used) || 0,
              lot_number: lot.lot_number || lineProduction?.lot_number,
              batch_number: lot.batch_number || lineProduction?.batch_number
            }))
            .filter(u => u.quantity > 0)
        : [{
            itemId,
            lotId: null,
            quantity: actualQuantity,
            lot_number: lineProduction?.lot_number,
            batch_number: lineProduction?.batch_number
          }];

      // Fallback: if lots existed but all had zero quantity, use actualQuantity
      if (usages.length === 0 && actualQuantity > 0) {
        usages = [{
          itemId,
          lotId: null,
          quantity: actualQuantity,
          lot_number: lineProduction?.lot_number,
          batch_number: lineProduction?.batch_number
        }];
      }

      if (usages.length === 0) continue;

      try {
        await config.executeUsage(lineProduction.company_id, {
          locationId: lineProduction.production_location,
          usages,
          notes: `Line production: Batch ${lineProduction?.batch_number || 'N/A'}`
        });
        totalTransactionCount += usages.length;
      } catch (error) {
        console.error(`Failed to create usage transaction for ${supplyName}:`, error);
        errors.push(`${supplyName}: ${error.message}`);
      }
    }

    return { success: errors.length === 0, errors, transactionCount: totalTransactionCount };
  };

  const handleApproveLineProduction = async () => {
    // Check permissions before proceeding
    if (!canApprove) {
      showSnackbar('You do not have permission to approve line production.', 'error');
      return;
    }

    if (!lineProduction?.id) {
      showSnackbar('Line production not found', 'error');
      return;
    }
    
    const completed = await checkAllStepsCompleted();
    if (!completed) {
      showSnackbar('Please complete all previous steps before approving the line production', 'error');
      return;
    }
    
    setApprovalDialogOpen(true);
    setInventoryCheckLoading(true);
    
    try {
      // Check inventory availability for logged supplies
      const { hasEnoughInventory: inventoryAvailable, insufficientItems } = await checkInventoryAvailability(summaryData.supplies);
      
      setHasEnoughInventory(inventoryAvailable);
      setInsufficientInventoryItems(insufficientItems || []);
      
      if (!inventoryAvailable) {
        console.warn('Insufficient inventory for approval:', insufficientItems);
      }
    } catch (error) {
      console.error('Error checking inventory for approval:', error);
      setHasEnoughInventory(false);
      setInsufficientInventoryItems([]);
    } finally {
      setInventoryCheckLoading(false);
    }
  };

  const handleConfirmApproval = async () => {
    if (!approvalSignature.trim()) {
      showSnackbar('Please provide your signature to approve the line production', 'error');
      return;
    }

    setApproving(true);
    try {
      const lineProductionId = String(lineProduction.id);
      
      // Final safety check - re-verify inventory availability (in case it changed since dialog opened)
      const { hasEnoughInventory, insufficientItems } = await checkInventoryAvailability(summaryData.supplies);
      
      if (!hasEnoughInventory && shouldCreateUsageTransactions) {
        console.error('Inventory changed since dialog opened - insufficient supplies:', insufficientItems);
        showSnackbar('Inventory levels changed - insufficient supplies to approve production. Please close and reopen the approval dialog.', 'error');
        setApproving(false);
        return;
      }

      // Create usage transactions for supplies only if requested
      let usageTransactionCount = 0;
      if (shouldCreateUsageTransactions) {
        const { success: usageSuccess, errors: usageErrors, transactionCount } = await createUsageTransactions(summaryData.supplies);
        
        if (!usageSuccess) {
          console.error('Usage transaction errors:', usageErrors);
          showSnackbar(`Failed to create usage transactions: ${usageErrors.join(', ')}`, 'error');
          setApproving(false);
          return;
        }
        
        usageTransactionCount = transactionCount;
      }

      // Create inventory adjustment for produced goods if requested
      let inventoryUnitsAdded = 0;
      if (shouldCreateInventoryAdjustment && selectedInventoryLocation) {
        const { success: inventorySuccess, error: inventoryError, unitsAdded } = await createInventoryAdjustment();
        
        if (!inventorySuccess) {
          console.error('Inventory creation error:', inventoryError);
          showSnackbar(`Failed to create inventory adjustment: ${inventoryError}`, 'error');
          setApproving(false);
          return;
        }
        
        inventoryUnitsAdded = unitsAdded || 0;
      }
      
      // Update line production status to approved with signature
      const { error: updateError } = await supabase
        .from('line_productions')
        .update({ 
          status: 'completed',
          line_summary_signature: approvalSignature,
          line_summary_approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', lineProductionId);

      if (updateError) {
        console.error('Error updating line production status:', updateError);
        showSnackbar('Failed to approve line production', 'error');
        setApproving(false);
        return;
      }

      // Add to approval history
      const { error: historyError } = await supabase
        .from('line_production_approval_history')
        .insert({
          line_production_id: lineProductionId,
          action_type: 'approved',
          signature: approvalSignature,
          reason: null,
          performed_by: (await supabase.auth.getUser()).data.user?.id
        });

      if (historyError) {
        console.warn('Failed to log approval history:', historyError);
      }

      let successMessage = 'Line production approved successfully!';
      const messages = [];
      
      if (shouldCreateUsageTransactions) {
        messages.push(`${usageTransactionCount} supply usage transactions created`);
      }
      
      if (shouldCreateInventoryAdjustment && inventoryUnitsAdded > 0) {
        const locationName = availableLocations.find(loc => loc.id === selectedInventoryLocation)?.name || 'selected location';
        messages.push(`${inventoryUnitsAdded.toLocaleString()} units added to inventory at ${locationName}`);
      }
      
      if (messages.length > 0) {
        successMessage += ` ${messages.join(' and ')}.`;
      }
      
      showSnackbar(successMessage, 'success');
      setApprovalDialogOpen(false);
      setApprovalSignature('');
      setSignaturePreviewName('');
      
      // Refresh data
      fetchApprovalHistory();
      
      // Notify parent component to refresh
      if (onUpdate) {
        const updatedLineProduction = { 
          ...lineProduction, 
          id: lineProductionId,
          status: 'completed',
          line_summary_signature: approvalSignature,
          line_summary_approved_at: new Date().toISOString()
        };
        onUpdate(updatedLineProduction);
      }
    } catch (error) {
      console.error('Error approving line production:', error);
      showSnackbar('An error occurred while approving the line production', 'error');
    } finally {
      setApproving(false);
    }
  };

  const handleUnapproveLineProduction = () => {
    // Check permissions before proceeding
    if (!canApprove) {
      showSnackbar('You do not have permission to unapprove line production.', 'error');
      return;
    }
    
    setUnapproveDialogOpen(true);
  };

  const handleConfirmUnapproval = async () => {
    if (!unapprovalSignature.trim()) {
      showSnackbar('Please provide your signature to unapprove the line production', 'error');
      return;
    }

    if (!unapprovalReason.trim()) {
      showSnackbar('Please provide a reason for unapproving the line production', 'error');
      return;
    }

    setUnapproving(true);
    try {
      const lineProductionId = String(lineProduction.id);
      
      // Update line production status back to pending_approval
      const { error: updateError } = await supabase
        .from('line_productions')
        .update({ 
          status: 'pending_approval',
          line_summary_signature: null,
          line_summary_approved_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', lineProductionId);

      if (updateError) {
        console.error('Error updating line production status:', updateError);
        showSnackbar('Failed to unapprove line production', 'error');
        setUnapproving(false);
        return;
      }

      // Add to approval history
      const { error: historyError } = await supabase
        .from('line_production_approval_history')
        .insert({
          line_production_id: lineProductionId,
          action_type: 'unapproved',
          signature: unapprovalSignature,
          reason: unapprovalReason,
          performed_by: (await supabase.auth.getUser()).data.user?.id
        });

      if (historyError) {
        console.warn('Failed to log unapproval history:', historyError);
      }
      
      showSnackbar('Line production unapproved successfully', 'success');
      setUnapproveDialogOpen(false);
      setUnapprovalSignature('');
      setUnapprovalReason('');
      setUnapprovalSignaturePreview('');
      
      // Refresh data
      fetchApprovalHistory();
      
      // Notify parent component to refresh
      if (onUpdate) {
        const updatedLineProduction = { 
          ...lineProduction, 
          id: lineProductionId,
          status: 'pending_approval',
          line_summary_signature: null,
          line_summary_approved_at: null
        };
        onUpdate(updatedLineProduction);
      }
    } catch (error) {
      console.error('Error unapproving line production:', error);
      showSnackbar('An error occurred while unapproving the line production', 'error');
    } finally {
      setUnapproving(false);
    }
  };

  const handleApprovalPageChange = (event, newPage) => {
    setApprovalPage(newPage);
  };

  const handleApprovalRowsPerPageChange = (event) => {
    setApprovalRowsPerPage(parseInt(event.target.value, 10));
    setApprovalPage(0);
  };

  // Location selection dialog handlers
  const handleOpenInventoryLocationDialog = () => {
    setInventoryLocationDialogOpen(true);
    setInventoryLocationSearchTerm('');
    setDialogSelectedInventoryLocation(null);
  };

  const handleCloseInventoryLocationDialog = () => {
    setInventoryLocationDialogOpen(false);
    setInventoryLocationSearchTerm('');
    setDialogSelectedInventoryLocation(null);
  };

  const handleDialogInventoryLocationSelect = (location) => {
    setDialogSelectedInventoryLocation(location);
  };

  const handleConfirmInventoryLocationSelection = () => {
    if (dialogSelectedInventoryLocation) {
      setSelectedInventoryLocation(dialogSelectedInventoryLocation.id);
      setInventoryLocationDialogOpen(false);
      setInventoryLocationSearchTerm('');
      setDialogSelectedInventoryLocation(null);
    }
  };

  const getFilteredLocationsForInventory = () => {
    const locations = !inventoryLocationSearchTerm 
      ? availableLocations 
      : (() => {
          const searchLower = inventoryLocationSearchTerm.toLowerCase();
          return availableLocations.filter(location => 
            location.name.toLowerCase().includes(searchLower) ||
            (location.city && location.city.toLowerCase().includes(searchLower)) ||
            (location.state && location.state.toLowerCase().includes(searchLower))
          );
        })();
    
    // Ensure main location is always first, then alphabetically
    return locations.sort((a, b) => {
      if (a.is_main && !b.is_main) return -1;
      if (!a.is_main && b.is_main) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  const getSelectedInventoryLocationName = () => {
    if (!selectedInventoryLocation) return 'Select Location';
    const location = availableLocations.find(l => l.id === selectedInventoryLocation);
    return location ? location.name : 'Unknown Location';
  };

  const calculateProductionDuration = () => {
    if (!summaryData.lineLog?.start_time) return null;
    
    try {
      const startTime = dayjs(summaryData.lineLog.start_time);
      if (!startTime.isValid()) return null;
      
      const endTime = summaryData.yieldData?.production_end_time 
        ? dayjs(summaryData.yieldData.production_end_time)
        : dayjs();
      
      if (!endTime.isValid()) return null;
      
      const durationInMinutes = endTime.diff(startTime, 'minute');
      const hours = Math.floor(durationInMinutes / 60);
      const minutes = durationInMinutes % 60;
      
      return { hours, minutes, totalMinutes: durationInMinutes };
    } catch (error) {
      console.error('Error calculating duration:', error);
      return null;
    }
  };

  const calculateUnitsBreakdown = () => {
    if (!summaryData.unitConfigurations || summaryData.unitConfigurations.length === 0) {
      return { pallets: 0, cases: 0, individualUnits: 0 };
    }

    const config = summaryData.unitConfigurations[0]; // Use most recent config
    const totalUnits = lineProduction?.planned_quantity || 0;
    
    if (!config || !totalUnits) {
      return { pallets: 0, cases: 0, individualUnits: totalUnits };
    }

    const unitsPerCase = config.units_per_case || 1;
    const casesPerPallet = config.cases_per_pallet || 1;
    const unitsPerPallet = unitsPerCase * casesPerPallet;

    const pallets = Math.floor(totalUnits / unitsPerPallet);
    const remainingAfterPallets = totalUnits % unitsPerPallet;
    const cases = Math.floor(remainingAfterPallets / unitsPerCase);
    const individualUnits = remainingAfterPallets % unitsPerCase;

    return { pallets, cases, individualUnits };
  };

  const calculateActualProductionVolume = () => {
    if (!lineProduction) {
      return null;
    }
    
    const targetQuantity = lineProduction.planned_quantity || 0;
    const targetUnit = lineProduction.planned_unit_of_measure || 'pieces';
    
    return {
      volume: targetQuantity,
      unit: targetUnit,
      originalSize: targetQuantity,
      originalUnit: targetUnit
    };
  };

  const handlePrintSummary = () => {
    const actualVolume = calculateActualProductionVolume();
    const duration = calculateProductionDuration();

    // Set loading to true before printing
    setLoading(true);
    
      const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Line Production Summary - ${lineProduction?.batch_number || 'Production Line'}</title>
          <style>
            @page {
              size: auto;
              margin: 20mm 15mm 20mm 15mm;
              @top-left { content: ""; }
              @top-center { content: ""; }
              @top-right { content: ""; }
              @bottom-left { content: ""; }
              @bottom-center { content: ""; }
              @bottom-right { content: ""; }
            }
            @media print {
              body {
                padding: 0 !important;
                -webkit-print-color-adjust: exact;
                font-size: 10pt;
              }
              .no-print {
                display: none !important;
              }
              .page-break {
                page-break-before: always;
              }
              .avoid-break {
                page-break-inside: avoid;
              }
            }

            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
              line-height: 1.5;
              color: #1a1a1a;
              background: white;
              padding: 20px;
              font-size: 14px;
            }
            
            .header {
              text-align: center;
              padding-bottom: 32px;
              margin-bottom: 40px;
              border-bottom: 2px solid #e5e5e5;
            }
            
            .header h1 {
              color: #1a1a1a;
              font-size: 32px;
              font-weight: 600;
              margin-bottom: 12px;
              letter-spacing: -0.5px;
            }
            
            .header p {
              color: #6b7280;
              font-size: 16px;
              font-weight: 400;
              margin: 4px 0;
            }
            
            .section {
              margin-bottom: 40px;
              break-inside: avoid;
            }
            
            .section-title {
              background: #f8fafc;
              color: #374151;
              padding: 16px 24px;
              margin-bottom: 24px;
              font-size: 18px;
              font-weight: 600;
              border-left: 4px solid #000000;
              border-radius: 0 8px 8px 0;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }
            
            .info-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
              gap: 20px;
              margin-bottom: 24px;
            }
            
            .info-item {
              background: #ffffff;
              padding: 20px;
              border-radius: 8px;
              border: 1px solid #e5e7eb;
              box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
            }
            
            .info-label {
              font-weight: 600;
              color: #6b7280;
              font-size: 13px;
              margin-bottom: 8px;
              letter-spacing: 0.5px;
            }
            
            .info-value {
              font-size: 16px;
              color: #1f2937;
              font-weight: 500;
              line-height: 1.4;
            }
            
            .cost-summary {
              background: #f8fafc;
              padding: 32px;
              border-radius: 12px;
              border: 1px solid #e5e7eb;
              margin: 32px 0;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }
            
            .cost-total {
              font-size: 28px;
              font-weight: 600;
              color: #1f2937;
              text-align: center;
              letter-spacing: -0.5px;
            }
            
            .table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
              background: white;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }
            
            .table th {
              background: #f8fafc;
              padding: 16px;
              text-align: left;
              font-weight: 600;
              color: #374151;
              border-bottom: 1px solid #e5e7eb;
            }
            
            .table td {
              padding: 16px;
              border-bottom: 1px solid #e5e7eb;
              color: #1f2937;
            }
            
            .table tr:last-child td {
              border-bottom: none;
            }
            
            .consumption-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
              gap: 20px;
              margin: 20px 0;
            }
            
            .consumption-item {
              background: #ffffff;
              padding: 20px;
              border-radius: 8px;
              border: 1px solid #e5e7eb;
              box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
            }
            
            .consumption-title {
              font-weight: 600;
              color: #1f2937;
              font-size: 16px;
              margin-bottom: 12px;
            }
            
            .consumption-list {
              list-style: none;
            }
            
            .consumption-list li {
              padding: 8px 0;
              border-bottom: 1px solid #f3f4f6;
              color: #6b7280;
            }
            
            .consumption-list li:last-child {
              border-bottom: none;
            }
            
            .consumption-list strong {
              color: #1f2937;
            }
            
            .print-info {
              text-align: center;
              color: #9ca3af;
              font-size: 12px;
              margin-top: 48px;
              border-top: 1px solid #e5e7eb;
              padding-top: 24px;
              font-weight: 400;
            }
            
            .highlight-box {
              background: #f0fdf4;
              border: 2px solid #10b981;
              border-radius: 8px;
              padding: 20px;
              margin: 20px 0;
            }
            
            .highlight-title {
              font-weight: 600;
              color: #000000;
              font-size: 16px;
              margin-bottom: 8px;
            }

            .highlight-value {
              font-size: 24px;
              font-weight: 600;
              color: #10b981;
            }
            
            @media print {
              body { padding: 20px; }
              .section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Line Production Summary</h1>
            <p>Batch Number: ${lineProduction?.batch_number || 'Not Set'}</p>
            <p>Generated: ${new Date().toLocaleString()}</p>
            <p>Status: ${lineProduction?.status || 'Pending'} | Priority: ${lineProduction?.priority?.toUpperCase() || 'NORMAL'}</p>
          </div>

          <!-- Basic Information Section -->
          <div class="section">
            <div class="section-title">Basic Production Information</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Batch Number</div>
                <div class="info-value">${lineProduction?.batch_number || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Internal Code</div>
                <div class="info-value" style="font-family: monospace;">${lineProduction?.internal_code || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Lot Number</div>
                <div class="info-value">${lineProduction?.lot_number || 'Auto-Generated'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Status</div>
                <div class="info-value">${lineProduction?.status || 'Pending'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Priority</div>
                <div class="info-value">${lineProduction?.priority?.toUpperCase() || 'NORMAL'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Shift Supervisor</div>
                <div class="info-value">${lineProduction?.shift_supervisor || 'Not Assigned'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Created Date</div>
                <div class="info-value">${formatDate(lineProduction?.created_at)}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Production Line</div>
                <div class="info-value">${summaryData.lineLog?.production_line || 'Not Set'}</div>
              </div>
            </div>
          </div>

          <!-- Product & Template Information -->
          <div class="section">
            <div class="section-title">Product & Template Details</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Product Name</div>
                <div class="info-value">${summaryData.product?.name || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Product SKU</div>
                <div class="info-value">${summaryData.product?.sku || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Product Type</div>
                <div class="info-value">${summaryData.product?.product_type || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Product Variant</div>
                <div class="info-value">${summaryData.productVariant ? 
                  `${summaryData.productVariant.volume_weight}${summaryData.productVariant.volume_unit}` :
                  lineProduction?.product_variant || 'Not Set'
                }</div>
              </div>
              <div class="info-item">
                <div class="info-label">Template Name</div>
                <div class="info-value">${summaryData.template?.template_name || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Template Description</div>
                <div class="info-value">${summaryData.template?.description || 'Not Set'}</div>
              </div>
            </div>
          </div>

          <!-- Location & Timing -->
          <div class="section">
            <div class="section-title">Location & Timing Information</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Production Location</div>
                <div class="info-value">${summaryData.location?.name || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Location City</div>
                <div class="info-value">${summaryData.location?.city || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Location State</div>
                <div class="info-value">${summaryData.location?.state || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Facility Type</div>
                <div class="info-value">${summaryData.location?.facility_type || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Production Start Time</div>
                <div class="info-value">${summaryData.lineLog?.start_time ? formatDateTime(summaryData.lineLog.start_time) : 'Not Started'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Production End Time</div>
                <div class="info-value">${summaryData.yieldData?.production_end_time ? formatDateTime(summaryData.yieldData.production_end_time) : 'In Progress'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Total Duration</div>
                <div class="info-value">${duration ? `${duration.hours} hours ${duration.minutes} minutes` : 'Calculating...'}</div>
              </div>
            </div>
          </div>

          <!-- Production Quantities -->
          <div class="section">
            <div class="section-title">Production Quantities</div>
            <div class="highlight-box" style="border: 2px solid #10b981; background: #f0fdf4;">
              <div class="highlight-title">Total Units Produced</div>
              <div class="highlight-value">${(summaryData.yieldData?.total_units_produced || lineProduction?.planned_quantity || 0).toLocaleString()}</div>
            </div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Planned Quantity</div>
                <div class="info-value">${lineProduction?.planned_quantity?.toLocaleString() || 0}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Planned Unit of Measure</div>
                <div class="info-value">${lineProduction?.planned_unit_of_measure || 'pieces'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Actual Units Produced</div>
                <div class="info-value">${summaryData.yieldData?.total_units_produced?.toLocaleString() || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Pallet Quantity</div>
                <div class="info-value">${lineProduction?.pallet_quantity?.toLocaleString() || 0}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Case Quantity</div>
                <div class="info-value">${lineProduction?.case_quantity?.toLocaleString() || 0}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Unit Quantity</div>
                <div class="info-value">${lineProduction?.unit_quantity?.toLocaleString() || 0}</div>
              </div>
            </div>
          </div>

          <!-- Unit Configuration -->
          ${summaryData.unitConfiguration ? `
            <div class="section">
              <div class="section-title">Unit Configuration</div>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Units Per Case</div>
                  <div class="info-value">${summaryData.unitConfiguration.units_per_case?.toLocaleString() || 1}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Cases Per Pallet</div>
                  <div class="info-value">${summaryData.unitConfiguration.cases_per_pallet?.toLocaleString() || 1}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Units Per Pallet</div>
                  <div class="info-value">${((summaryData.unitConfiguration.units_per_case || 1) * (summaryData.unitConfiguration.cases_per_pallet || 1)).toLocaleString()}</div>
                </div>
              </div>
            </div>
          ` : ''}

          <!-- Template Consumption -->
          ${summaryData.templateConsumption && (
            summaryData.templateConsumption.caps.length > 0 ||
            summaryData.templateConsumption.bottles.length > 0 ||
            summaryData.templateConsumption.labels.length > 0 ||
            summaryData.templateConsumption.supplies.length > 0
          ) ? `
            <div class="section">
              <div class="section-title">Template Consumption Details</div>
              <div class="consumption-grid">
                ${summaryData.templateConsumption.caps.length > 0 ? `
                  <div class="consumption-item">
                    <div class="consumption-title">Caps</div>
                    <ul class="consumption-list">
                      ${summaryData.templateConsumption.caps.map(cap => `
                        <li><strong>${cap.caps?.name || 'Unknown Cap'}</strong><br>
                        SKU: ${cap.caps?.sku || 'N/A'}<br>
                        Quantity: ${cap.quantity_per_unit} per unit<br>
                        Color: ${cap.caps?.color || 'N/A'}<br>
                        Material: ${cap.caps?.material || 'N/A'}</li>
                      `).join('')}
                    </ul>
                  </div>
                ` : ''}
                
                ${summaryData.templateConsumption.bottles.length > 0 ? `
                  <div class="consumption-item">
                    <div class="consumption-title">Bottles</div>
                    <ul class="consumption-list">
                      ${summaryData.templateConsumption.bottles.map(bottle => `
                        <li><strong>${bottle.bottles?.name || 'Unknown Bottle'}</strong><br>
                        SKU: ${bottle.bottles?.sku || 'N/A'}<br>
                        Quantity: ${bottle.quantity_per_unit} per unit<br>
                        Capacity: ${bottle.bottles?.capacity || 'N/A'} ${bottle.bottles?.capacity_unit || ''}<br>
                        Color: ${bottle.bottles?.color || 'N/A'}<br>
                        Material: ${bottle.bottles?.material || 'N/A'}</li>
                      `).join('')}
                    </ul>
                  </div>
                ` : ''}
                
                ${summaryData.templateConsumption.labels.length > 0 ? `
                  <div class="consumption-item">
                    <div class="consumption-title">Labels</div>
                    <ul class="consumption-list">
                      ${summaryData.templateConsumption.labels.map(label => `
                        <li><strong>${label.labels?.name || 'Unknown Label'}</strong><br>
                        SKU: ${label.labels?.sku || 'N/A'}<br>
                        Quantity: ${label.quantity_per_unit} per unit<br>
                        Material: ${label.labels?.material || 'N/A'}<br>
                        Size: ${label.labels?.width_mm || 'N/A'}mm x ${label.labels?.height_mm || 'N/A'}mm</li>
                      `).join('')}
                    </ul>
                  </div>
                ` : ''}
                
                ${summaryData.templateConsumption.supplies.length > 0 ? `
                  <div class="consumption-item">
                    <div class="consumption-title">Other Supplies</div>
                    <ul class="consumption-list">
                      ${summaryData.templateConsumption.supplies.map(supply => `
                        <li><strong>${supply.supplies?.name || 'Unknown Supply'}</strong><br>
                        SKU: ${supply.supplies?.sku || 'N/A'}<br>
                        Quantity: ${supply.quantity_per_unit} per unit<br>
                        Unit: ${supply.supplies?.unit || 'N/A'}<br>
                        Unit Cost: ${formatCurrency(supply.supplies?.unit_cost)}</li>
                      `).join('')}
                    </ul>
                  </div>
                ` : ''}
              </div>
            </div>
          ` : ''}

          <!-- Actual Supplies Used -->
          ${summaryData.supplies.length > 0 ? `
            <div class="section">
              <div class="section-title">Supplies Actually Used in Production</div>
              <table class="table">
                <thead>
                  <tr>
                    <th>Supply Name</th>
                    <th>Type</th>
                    <th>Supplier</th>
                    <th>Quantity Used</th>
                  </tr>
                </thead>
                <tbody>
                  ${summaryData.supplies
                    .sort((a, b) => {
                      const typeOrder = {
                        'cap': 0, 'caps': 0,
                        'bottle': 1, 'bottles': 1,
                        'label': 2, 'labels': 2,
                        'supply': 3, 'supplies': 3
                      };
                      const orderA = typeOrder[a.item_type] ?? 999;
                      const orderB = typeOrder[b.item_type] ?? 999;
                      if (orderA === orderB) {
                        return (a.supply_name || '').localeCompare(b.supply_name || '');
                      }
                      return orderA - orderB;
                    })
                    .map(supply => `
                      <tr>
                        <td><strong>${supply.supply_name || 'Unknown Supply'}</strong></td>
                        <td>${supply.item_type || 'N/A'}</td>
                        <td>${supply.supplier_name || 'N/A'}</td>
                        <td>${supply.actual_quantity?.toLocaleString() || 0} units</td>
                      </tr>
                    `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          <!-- Quality Control Tests -->
          ${summaryData.qcTests.length > 0 ? `
            <div class="section">
              <div class="section-title">Quality Control Tests</div>
              <table class="table">
                <thead>
                  <tr>
                    <th>Test Date</th>
                    <th>Test Type</th>
                    <th>Result</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  ${summaryData.qcTests.map(test => `
                    <tr>
                      <td>${formatDateTime(test.created_at)}</td>
                      <td>${test.test_type || 'N/A'}</td>
                      <td>${test.result || 'N/A'}</td>
                      <td>${test.notes || 'No notes'}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          <!-- Signatures -->
          ${summaryData.signatures.length > 0 ? `
            <div class="section">
              <div class="section-title">Production Signatures</div>
              <table class="table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Name</th>
                    <th>Signed Date</th>
                  </tr>
                </thead>
                <tbody>
                  ${summaryData.signatures.map(signature => `
                    <tr>
                      <td><strong>${signature.role || 'N/A'}</strong></td>
                      <td>${signature.signature_name || 'N/A'}</td>
                      <td>${formatDateTime(signature.created_at)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          <!-- Cost Analysis Section -->
          ${summaryData.lineCosts ? `
            <div class="section">
              <div class="section-title">Complete Cost Analysis</div>
              <div class="cost-summary">
                <div class="cost-total">
                  Total Production Cost: ${formatCurrency(summaryData.lineCosts.total_production_batch_cost || summaryData.lineCosts.total_cost)}
                </div>
              </div>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Material Cost</div>
                  <div class="info-value">${formatCurrency(summaryData.lineCosts.total_material_cost)}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Marketing Cost</div>
                  <div class="info-value">${formatCurrency(summaryData.lineCosts.marketing_cost)}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Labor Cost</div>
                  <div class="info-value">${formatCurrency(summaryData.lineCosts.labor_cost)}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Overhead Cost</div>
                  <div class="info-value">${formatCurrency(summaryData.lineCosts.calculated_overhead_cost)}</div>
                </div>
              </div>
            </div>
          ` : ''}

          <!-- Yield Data -->
          ${summaryData.yieldData ? `
            <div class="section">
              <div class="section-title">Production Yield Data</div>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Total Units Produced</div>
                  <div class="info-value" style="font-weight: 600;">${summaryData.yieldData.total_units_produced?.toLocaleString() || 0}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Good Units</div>
                  <div class="info-value">${(() => {
                    const totalUnits = summaryData.yieldData.total_units_produced || 0;
                    const wasteUnits = summaryData.yieldData.units_set_aside || 0;
                    const goodUnits = totalUnits - wasteUnits;
                    return goodUnits.toLocaleString();
                  })()}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Waste (Defective Units)</div>
                  <div class="info-value" style="font-weight: 600;">${summaryData.yieldData.units_set_aside?.toLocaleString() || 0}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Yield Percentage</div>
                  <div class="info-value">${(() => {
                    const totalUnits = summaryData.yieldData.total_units_produced || 0;
                    const wasteUnits = summaryData.yieldData.units_set_aside || 0;
                    const goodUnits = totalUnits - wasteUnits;
                    const yieldPercentage = totalUnits > 0 ? (goodUnits / totalUnits) * 100 : 100;
                    return formatPercentage(yieldPercentage);
                  })()}</div>
                </div>
              </div>
            </div>
          ` : ''}

          <!-- Approval Information -->
          ${lineProduction?.line_summary_signature ? `
            <div class="section">
              <div class="section-title">Approval Information</div>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Approved By</div>
                  <div class="info-value">
                    <div style="font-family: 'Brush Script MT', 'Brush Script Std', cursive; font-size: 32px; color: #059669; text-shadow: 1px 1px 2px rgba(0,0,0,0.1);">
                      ${lineProduction.line_summary_signature}
                    </div>
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-label">Approval Date</div>
                  <div class="info-value">${formatDateTime(lineProduction.line_summary_approved_at)}</div>
                </div>
              </div>
            </div>
          ` : `
            <div class="section">
              <div class="section-title">Approval Status</div>
              <div class="highlight-box" style="background: #fef3c7; border: 1px solid #f59e0b;">
                <div class="highlight-title" style="color: #92400e;">Pending Approval</div>
                <div class="highlight-value" style="color: #92400e; font-size: 24px;">This line production has not been approved yet</div>
              </div>
            </div>
          `}

          <!-- Approval History -->
          ${approvalHistory && approvalHistory.length > 0 ? `
            <div class="section">
              <div class="section-title">Approval History</div>
              <table class="table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Signature</th>
                    <th>Performed By</th>
                    <th>Date & Time</th>
                    ${approvalHistory.some(entry => entry.reason) ? '<th>Reason</th>' : ''}
                  </tr>
                </thead>
                <tbody>
                  ${approvalHistory.slice(0, 10).map(entry => `
                    <tr>
                      <td><strong>${entry.action_type === 'approved' ? 'APPROVED' : 'UNAPPROVED'}</strong></td>
                      <td>
                        <div style="font-family: 'Brush Script MT', 'Brush Script Std', cursive; font-size: 18px; color: ${entry.action_type === 'approved' ? '#059669' : '#dc2626'}; text-shadow: 1px 1px 1px rgba(0,0,0,0.1);">
                          ${entry.signature}
                        </div>
                      </td>
                      <td>${entry.profiles?.full_name ||
                        (entry.profiles?.first_name && entry.profiles?.last_name ? `${entry.profiles.first_name} ${entry.profiles.last_name}` : '') ||
                        (entry.profiles?.first_name ? entry.profiles.first_name : '') ||
                        (entry.profiles?.last_name ? entry.profiles.last_name : '') ||
                        (entry.profiles?.email ? entry.profiles.email.split('@')[0] : '') ||
                        (entry.performed_by ? `User (${entry.performed_by.slice(0, 8)}...)` : 'Unknown User')}</td>
                      <td>${formatDateTime(entry.performed_at || entry.created_at)}</td>
                      ${entry.reason ? `<td>${entry.reason}</td>` : ''}
                    </tr>
                  `).join('')}
                </tbody>
              </table>
              ${approvalHistory.length > 10 ? `<p><em>Showing latest 10 entries of ${approvalHistory.length} total</em></p>` : ''}
            </div>
          ` : ''}
        </body>
      </html>
    `;

    const iframeId = 'production-line-print-frame';
    let iframe = document.getElementById(iframeId);

    if (iframe) {
      document.body.removeChild(iframe);
    }

    iframe = document.createElement('iframe');
    iframe.id = iframeId;
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0';
    iframe.style.height = '0';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

    iframeDoc.open();
    iframeDoc.write(printContent);
    iframeDoc.close();

    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();

        // Try to disable browser headers/footers
        try {
          const printSettings = {
            shouldPrintBackgrounds: true,
            shouldPrintSelectionOnly: false,
            mediaType: 'print',
            headerFooterEnabled: false
          };

          if (iframe.contentWindow.chrome && iframe.contentWindow.chrome.webstore) {
            iframe.contentWindow.print();
          } else {
            iframe.contentWindow.print();
          }
        } catch (e) {
          iframe.contentWindow.print();
        }

        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          setLoading(false);
        }, 1000);
      } catch (error) {
        console.error('Error printing:', error);
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        setLoading(false);
        showSnackbar('Error printing Production Line Summary', 'error');
      }
    };
  };

  // Permission check - must be after all hooks
  if (!hasPermission('production.line.assembly.summary.view', 'read')) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: 'calc(100vh - 200px)',
        textAlign: 'center',
        width: '100%',
        px: 3,
        py: 8
      }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <SummarizeIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Production Line Summary Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Production Line Summary. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
      </Box>
    );
  }

  // Check approve permissions  
  const canApprove = hasPermission('production.line.assembly.summary.approve', 'write');

  if (loading) {
    return (
      <Card sx={{ 
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[300]}`,
        background: 'white',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
        }
      }}>
        <CardContent sx={{ p: 6, textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'inline-flex',
              p: 3,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              mb: 2
            }}>
              <SummarizeIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            </Box>
          </Box>
          <Typography variant="h6" sx={{ 
            fontWeight: 500,
            color: 'primary.main',
            mb: 1,
            fontSize: '1.1rem'
          }}>
            Loading Line Production Summary
          </Typography>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontWeight: 500,
            maxWidth: 400,
            mx: 'auto',
            lineHeight: 1.6,
            mb: 3
          }}>
            Analyzing line production data, costs, and quality metrics...
          </Typography>
          <Box sx={{ 
            width: '60%', 
            mx: 'auto',
            '& .MuiLinearProgress-root': {
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(theme.palette.grey[300], 0.3),
              '& .MuiLinearProgress-bar': {
                bgcolor: 'primary.main',
                borderRadius: 4
              }
            }
          }}>
            <LinearProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Enhanced Header Section - Matching BatchSummary.jsx */}
      <Card 
        sx={{ 
          borderRadius: 3,
          mb: 4,
          border: `1px solid ${theme.palette.grey[300]}`,
          background: 'white',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          },
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <SummarizeIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                    Line Production Summary
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Complete overview of {lineProduction?.batch_number || 'Production Line'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            <Stack direction="row" spacing={2}>
              {lineProduction?.status !== 'completed' && allStepsCompleted && (
                <Button
                  variant="contained"
                  startIcon={<VerifiedIcon />}
                  onClick={handleApproveLineProduction}
                  disabled={approving || !canApprove}
                  sx={{
                    borderRadius: 3,
                    px: 3,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    color: 'white',
                    fontWeight: 500,
                    boxShadow: `0 6px 18px ${alpha(theme.palette.success.main, 0.25)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.35)}`
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.grey[400], 0.5),
                      color: 'text.disabled'
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {approving ? 'Approving...' : 'Approve Line Production'}
                </Button>
              )}
              
              {lineProduction?.status === 'completed' && (
                <Button
                  variant="outlined"
                  startIcon={<WarningIcon />}
                  onClick={handleUnapproveLineProduction}
                  disabled={!canApprove}
                  sx={{
                    borderRadius: 3,
                    px: 3,
                    py: 1.5,
                    borderColor: alpha(theme.palette.warning.main, 0.5),
                    color: theme.palette.warning.main,
                    fontWeight: 500,
                    background: 'white',
                    '&:hover': {
                      borderColor: theme.palette.warning.main,
                      backgroundColor: alpha(theme.palette.warning.main, 0.04),
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  Unapprove Line Production
                </Button>
              )}
              
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrintSummary}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                  background: 'white',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Print Summary
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Modern Dashboard Grid Layout */}
      <Grid container spacing={3}>
        
        {/* Core Information Row */}
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[300]}`,
            background: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            height: '100%',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <FactoryIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 1.5, fontSize: '0.9rem' }}>
                  Batch Info
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1, fontSize: '1.3rem' }}>
                {lineProduction?.batch_number || 'Not Set'}
              </Typography>
              <Chip 
                label={lineProduction?.status || 'Pending'}
                size="small"
                sx={{ 
                  fontWeight: 500,
                  borderRadius: 2,
                  background: lineProduction?.status === 'completed' 
                    ? theme.palette.success.main
                    : theme.palette.primary.main,
                  color: 'white',
                  mb: 0.5,
                  fontSize: '0.7rem'
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                Priority: {lineProduction?.priority?.toUpperCase() || 'NORMAL'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[300]}`,
            background: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            height: '100%',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <LocationIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 1.5, fontSize: '0.9rem' }}>
                  Location
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1, fontSize: '1.3rem' }}>
                {summaryData.location?.name || 'Not Set'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                {summaryData.location?.city}, {summaryData.location?.state}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                {summaryData.location?.facility_type}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[300]}`,
            background: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            height: '100%',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <TimerIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 1.5, fontSize: '0.9rem' }}>
                  Duration
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1, fontSize: '1.3rem' }}>
                {(() => {
                  const duration = calculateProductionDuration();
                  return duration ? `${duration.hours}h ${duration.minutes}m` : 'In Progress';
                })()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                Production Time
              </Typography>
              {summaryData.yieldData?.production_end_time && (
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                  Completed
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[300]}`,
            background: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            height: '100%',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <InventoryIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 1.5, fontSize: '0.9rem' }}>
                  Total Units
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1, fontSize: '1.3rem' }}>
                {(summaryData.yieldData?.total_units_produced || lineProduction?.planned_quantity || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                {summaryData.yieldData?.total_units_produced ? 'Actual Produced' : 'Planned Quantity'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[300]}`,
            background: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            height: '100%',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <CostIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 1.5, fontSize: '0.9rem' }}>
                  Total Cost
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1, fontSize: '1.3rem' }}>
                {formatCurrency(summaryData.lineCosts?.total_production_batch_cost || summaryData.lineCosts?.total_cost)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                Production Cost
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {/* Large Sections Grid */}
        <Grid item xs={12}>
          <Stack spacing={3}>
            
            {/* Product Details */}
            <Accordion 
              expanded={accordionState.productDetails} 
              onChange={(event, expanded) => updateAccordionState('productDetails', expanded)}
              elevation={0}
              sx={{
                borderRadius: '20px !important',
                border: `1px solid ${theme.palette.grey[300]}`,
                background: 'white',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                '&:before': { display: 'none' },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                },
                '&.Mui-expanded': {
                  margin: '16px 0 16px 0',
                  borderRadius: '20px !important',
                  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)' }
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiAccordion-region': { borderRadius: '0 0 20px 20px !important' },
                '& .MuiAccordionDetails-root': { borderRadius: '0 0 20px 20px !important' }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                sx={{
                  borderRadius: accordionState.productDetails ? '20px 20px 0 0 !important' : '20px !important',
                  background: alpha(theme.palette.primary.main, 0.04),
                  border: 'none',
                  minHeight: 72,
                  '&.Mui-expanded': { minHeight: 72, borderRadius: '20px 20px 0 0 !important' },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                    alignItems: 'center',
                    '&.Mui-expanded': { margin: '16px 0' }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <DescriptionIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Product & Template Details
                  </Typography>
                  <Chip 
                    label={summaryData.product ? 'Configured' : 'Not Set'} 
                    size="small" 
                    sx={{ 
                      ml: 'auto', mr: 2, fontWeight: 500, borderRadius: 2,
                      background: summaryData.product ? theme.palette.success.main : theme.palette.warning.main,
                      color: 'white'
                    }} 
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.grey[300]}`, 
                      borderRadius: 3,
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                        Product Information
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Product Name</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{summaryData.product?.name || 'Not Set'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>SKU</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{summaryData.product?.sku || 'Not Set'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Product Variant</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {summaryData.productVariant ? 
                              `${summaryData.productVariant.volume_weight}${summaryData.productVariant.volume_unit}` :
                              lineProduction?.product_variant || 'Not Set'
                            }
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Product Type</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{summaryData.product?.product_type || 'Not Set'}</Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.grey[300]}`, 
                      borderRadius: 3,
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                        Template Information
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Template Name</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{summaryData.template?.template_name || 'Not Set'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Description</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{summaryData.template?.description || 'Not Set'}</Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                  
                  {/* Template Consumption Section */}
                  <Grid item xs={12}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.grey[300]}`, 
                      borderRadius: 3,
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary }}>
                        Template Consumption
                      </Typography>
                      <Grid container spacing={3}>
                        
                        {/* Caps */}
                        <Grid item xs={12} md={6} lg={3}>
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: alpha(theme.palette.primary.main, 0.04), 
                            borderRadius: 2, 
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                              bgcolor: alpha(theme.palette.primary.main, 0.06),
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                            }
                          }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                              Caps:
                            </Typography>
                            {summaryData.templateConsumption.caps.length > 0 ? (
                              <Stack spacing={1}>
                                {summaryData.templateConsumption.caps.map((cap, index) => (
                                  <Box key={index}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {cap.caps?.name || 'Unknown Cap'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', ml: 1 }}>
                                      Qty: {cap.quantity_per_unit} per unit
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No caps configured</Typography>
                            )}
                          </Box>
                        </Grid>

                        {/* Bottles */}
                        <Grid item xs={12} md={6} lg={3}>
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: alpha(theme.palette.primary.main, 0.04), 
                            borderRadius: 2, 
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                              bgcolor: alpha(theme.palette.primary.main, 0.06),
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                            }
                          }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                              Bottles:
                            </Typography>
                            {summaryData.templateConsumption.bottles.length > 0 ? (
                              <Stack spacing={1}>
                                {summaryData.templateConsumption.bottles.map((bottle, index) => (
                                  <Box key={index}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {bottle.bottles?.name || 'Unknown Bottle'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', ml: 1 }}>
                                      Qty: {bottle.quantity_per_unit} per unit
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No bottles configured</Typography>
                            )}
                          </Box>
                        </Grid>

                        {/* Labels */}
                        <Grid item xs={12} md={6} lg={3}>
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: alpha(theme.palette.primary.main, 0.04), 
                            borderRadius: 2, 
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                              bgcolor: alpha(theme.palette.primary.main, 0.06),
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                            }
                          }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                              Labels:
                            </Typography>
                            {summaryData.templateConsumption.labels.length > 0 ? (
                              <Stack spacing={1}>
                                {summaryData.templateConsumption.labels.map((label, index) => (
                                  <Box key={index}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {label.labels?.name || 'Unknown Label'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', ml: 1 }}>
                                      Qty: {label.quantity_per_unit} per unit
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No labels configured</Typography>
                            )}
                          </Box>
                        </Grid>

                        {/* Supplies */}
                        <Grid item xs={12} md={6} lg={3}>
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: alpha(theme.palette.primary.main, 0.04), 
                            borderRadius: 2, 
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                              bgcolor: alpha(theme.palette.primary.main, 0.06),
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                            }
                          }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                              All Supplies:
                            </Typography>
                            {summaryData.templateConsumption.supplies.length > 0 ? (
                              <Stack spacing={1}>
                                {summaryData.templateConsumption.supplies.map((supply, index) => (
                                  <Box key={index}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {supply.supplies?.name || 'Unknown Supply'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', ml: 1 }}>
                                      Qty: {supply.quantity_per_unit} per unit
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No supplies configured</Typography>
                            )}
                          </Box>
                        </Grid>

                      </Grid>
                    </Card>
                  </Grid>
                  
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Schedule & Timing */}
            <Accordion 
              expanded={accordionState.schedule} 
              onChange={(event, expanded) => updateAccordionState('schedule', expanded)}
              elevation={0}
              sx={{
                borderRadius: '20px !important',
                border: `1px solid ${theme.palette.grey[300]}`,
                background: 'white',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                '&:before': { display: 'none' },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                },
                '&.Mui-expanded': {
                  margin: '16px 0 16px 0',
                  borderRadius: '20px !important',
                  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)' }
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiAccordion-region': { borderRadius: '0 0 20px 20px !important' },
                '& .MuiAccordionDetails-root': { borderRadius: '0 0 20px 20px !important' }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                sx={{
                  borderRadius: accordionState.schedule ? '20px 20px 0 0 !important' : '20px !important',
                  background: alpha(theme.palette.primary.main, 0.04),
                  border: 'none',
                  minHeight: 72,
                  '&.Mui-expanded': { minHeight: 72, borderRadius: '20px 20px 0 0 !important' },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                    alignItems: 'center',
                    '&.Mui-expanded': { margin: '16px 0' }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <ScheduleIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Schedule & Timing
                  </Typography>
                  <Chip 
                    label={summaryData.yieldData?.production_end_time ? 'Completed' : 'In Progress'} 
                    size="small" 
                    sx={{ 
                      ml: 'auto', mr: 2, fontWeight: 500, borderRadius: 2,
                      background: summaryData.yieldData?.production_end_time ? theme.palette.success.main : theme.palette.warning.main,
                      color: 'white'
                    }} 
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.grey[300]}`, 
                      borderRadius: 3,
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                        Creation & Start
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Created Date</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDate(lineProduction?.created_at)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Production Start</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {summaryData.lineLog?.start_time ? formatDateTime(summaryData.lineLog.start_time) : 'Not Started'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.grey[300]}`, 
                      borderRadius: 3,
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                        Completion & Duration
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Production End</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {summaryData.yieldData?.production_end_time ? formatDateTime(summaryData.yieldData.production_end_time) : 'In Progress'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Total Duration</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {(() => {
                              const duration = calculateProductionDuration();
                              return duration ? `${duration.hours} hours ${duration.minutes} minutes` : 'Calculating...';
                            })()}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.grey[300]}`, 
                      borderRadius: 3,
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                        Location Details
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Facility</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{summaryData.location?.name || 'Not Set'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Production Line</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{summaryData.lineLog?.production_line || 'Not Set'}</Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Unit Configuration */}
            <Accordion 
              expanded={accordionState.unitConfig} 
              onChange={(event, expanded) => updateAccordionState('unitConfig', expanded)}
              elevation={0}
              sx={{
                borderRadius: '20px !important',
                border: `1px solid ${theme.palette.grey[300]}`,
                background: 'white',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                '&:before': { display: 'none' },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                },
                '&.Mui-expanded': {
                  margin: '16px 0 16px 0',
                  borderRadius: '20px !important',
                  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)' }
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiAccordion-region': { borderRadius: '0 0 20px 20px !important' },
                '& .MuiAccordionDetails-root': { borderRadius: '0 0 20px 20px !important' }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                sx={{
                  borderRadius: accordionState.unitConfig ? '20px 20px 0 0 !important' : '20px !important',
                  background: alpha(theme.palette.primary.main, 0.04),
                  border: 'none',
                  minHeight: 72,
                  '&.Mui-expanded': { minHeight: 72, borderRadius: '20px 20px 0 0 !important' },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                    alignItems: 'center',
                    '&.Mui-expanded': { margin: '16px 0' }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <InventoryIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Unit Configuration & Production Breakdown
                  </Typography>
                  <Chip 
                    label={summaryData.unitConfiguration ? 'Configured' : 'Default'} 
                    size="small" 
                    sx={{ 
                      ml: 'auto', mr: 2, fontWeight: 500, borderRadius: 2,
                      background: summaryData.unitConfiguration ? theme.palette.success.main : theme.palette.warning.main,
                      color: 'white'
                    }} 
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.grey[300]}`, 
                      borderRadius: 3,
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                        Unit Configuration
                      </Typography>
                      {summaryData.unitConfiguration ? (
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Units Per Case</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>{summaryData.unitConfiguration.units_per_case?.toLocaleString() || 1}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Cases Per Pallet</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>{summaryData.unitConfiguration.cases_per_pallet?.toLocaleString() || 1}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Units Per Pallet</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {((summaryData.unitConfiguration.units_per_case || 1) * (summaryData.unitConfiguration.cases_per_pallet || 1)).toLocaleString()}
                            </Typography>
                          </Box>
                        </Stack>
                      ) : (
                        <Alert severity="info">Using default configuration (1 unit per case, 1 case per pallet)</Alert>
                      )}
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.grey[300]}`, 
                      borderRadius: 3,
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                        Production Breakdown
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Pallets</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                            {lineProduction?.pallet_quantity?.toLocaleString() || '0'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Cases</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                            {lineProduction?.case_quantity?.toLocaleString() || '0'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Individual Units</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                            {lineProduction?.unit_quantity?.toLocaleString() || '0'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Batch, Lot & Supervisor Information */}
            <Accordion 
              expanded={accordionState.batchInfo} 
              onChange={(event, expanded) => updateAccordionState('batchInfo', expanded)}
              elevation={0}
              sx={{
                borderRadius: '20px !important',
                border: `1px solid ${theme.palette.grey[300]}`,
                background: 'white',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                '&:before': { display: 'none' },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                },
                '&.Mui-expanded': {
                  margin: '16px 0 16px 0',
                  borderRadius: '20px !important',
                  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)' }
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiAccordion-region': { borderRadius: '0 0 20px 20px !important' },
                '& .MuiAccordionDetails-root': { borderRadius: '0 0 20px 20px !important' }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                sx={{
                  borderRadius: accordionState.batchInfo ? '20px 20px 0 0 !important' : '20px !important',
                  background: alpha(theme.palette.primary.main, 0.04),
                  border: 'none',
                  minHeight: 72,
                  '&.Mui-expanded': { minHeight: 72, borderRadius: '20px 20px 0 0 !important' },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                    alignItems: 'center',
                    '&.Mui-expanded': { margin: '16px 0' }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <EngineeringIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Batch, Lot & Supervisor Information
                  </Typography>
                  <Chip 
                    label={lineProduction?.internal_code ? 'Code Set' : 'No Code'} 
                    size="small" 
                    sx={{ 
                      ml: 'auto', mr: 2, fontWeight: 500, borderRadius: 2,
                      background: lineProduction?.internal_code ? theme.palette.success.main : theme.palette.warning.main,
                      color: 'white'
                    }} 
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.grey[300]}`, 
                      borderRadius: 3,
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                        Batch Information
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Batch Number</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{lineProduction?.batch_number || 'Not Set'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Internal Code</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                            {lineProduction?.internal_code || 'Not Set'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Status</Typography>
                          <Chip 
                            label={lineProduction?.status || 'Pending'}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              borderRadius: 2,
                              background: lineProduction?.status === 'completed' 
                                ? theme.palette.success.main
                                : theme.palette.warning.main,
                              color: 'white'
                            }}
                          />
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.grey[300]}`, 
                      borderRadius: 3,
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                        Lot Information
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Lot Number</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {lineProduction?.lot_number || 'Auto-Generated'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ 
                      p: 3, 
                      border: `1px solid ${theme.palette.grey[300]}`, 
                      borderRadius: 3,
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                        Supervisor Information
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Shift Supervisor</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {lineProduction?.shift_supervisor || 'Not Assigned'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>


            {/* Supplies Used */}
            <Accordion 
              expanded={accordionState.supplies} 
              onChange={(event, expanded) => updateAccordionState('supplies', expanded)}
              elevation={0}
              sx={{
                borderRadius: '20px !important',
                border: `1px solid ${theme.palette.grey[300]}`,
                background: 'white',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                '&:before': { display: 'none' },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                },
                '&.Mui-expanded': {
                  margin: '16px 0 16px 0',
                  borderRadius: '20px !important',
                  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)' }
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiAccordion-region': { borderRadius: '0 0 20px 20px !important' },
                '& .MuiAccordionDetails-root': { borderRadius: '0 0 20px 20px !important' }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                sx={{
                  borderRadius: accordionState.supplies ? '20px 20px 0 0 !important' : '20px !important',
                  background: alpha(theme.palette.primary.main, 0.04),
                  border: 'none',
                  minHeight: 72,
                  '&.Mui-expanded': { minHeight: 72, borderRadius: '20px 20px 0 0 !important' },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                    alignItems: 'center',
                    '&.Mui-expanded': { margin: '16px 0' }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <LocalShippingIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Supplies Used
                  </Typography>
                  <Chip 
                    label={`${summaryData.supplies.length} supplies`} 
                    size="small" 
                    sx={{ 
                      ml: 'auto', mr: 2, fontWeight: 500, borderRadius: 2,
                      background: theme.palette.primary.main, color: 'white'
                    }} 
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 4 }}>
                {summaryData.supplies.length > 0 ? (
                  <Stack spacing={2}>
                    {summaryData.supplies
                      .sort((a, b) => {
                        // Define the preferred order: caps → bottles → labels → supplies
                        const typeOrder = {
                          'cap': 0,
                          'caps': 0,
                          'bottle': 1,
                          'bottles': 1,
                          'label': 2,
                          'labels': 2,
                          'supply': 3,
                          'supplies': 3
                        };
                        
                        const orderA = typeOrder[a.item_type] ?? 999;
                        const orderB = typeOrder[b.item_type] ?? 999;
                        
                        // If same type order, sort by supply name
                        if (orderA === orderB) {
                          const nameA = a.supply_name || '';
                          const nameB = b.supply_name || '';
                          return nameA.localeCompare(nameB);
                        }
                        
                        return orderA - orderB;
                      })
                      .map((supply, index) => (
                      <Paper key={index} sx={{ 
                        p: 3, 
                        background: 'white',
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.grey[300]}`,
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
                        },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                          {supply.supply_name || 'Unknown Supply'}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Supplier: {supply.supplier_name || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Quantity: {supply.actual_quantity?.toLocaleString() || 0} units
                          </Typography>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info">No supplies logged yet</Alert>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Approval History */}
            <Accordion 
              expanded={accordionState.approvalHistory} 
              onChange={(event, expanded) => updateAccordionState('approvalHistory', expanded)}
              elevation={0}
              sx={{
                borderRadius: '20px !important',
                border: `1px solid ${theme.palette.grey[300]}`,
                background: 'white',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                '&:before': { display: 'none' },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                },
                '&.Mui-expanded': {
                  margin: '16px 0 16px 0',
                  borderRadius: '20px !important',
                  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)' }
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiAccordion-region': { borderRadius: '0 0 20px 20px !important' },
                '& .MuiAccordionDetails-root': { borderRadius: '0 0 20px 20px !important' }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                sx={{
                  borderRadius: accordionState.approvalHistory ? '20px 20px 0 0 !important' : '20px !important',
                  background: alpha(theme.palette.primary.main, 0.04),
                  border: 'none',
                  minHeight: 72,
                  '&.Mui-expanded': { minHeight: 72, borderRadius: '20px 20px 0 0 !important' },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                    alignItems: 'center',
                    '&.Mui-expanded': { margin: '16px 0' }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <VerifiedIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Approval History
                  </Typography>
                  <Chip 
                    label={`${approvalHistory.length} entries`} 
                    size="small" 
                    sx={{ 
                      ml: 'auto', mr: 2, fontWeight: 500, borderRadius: 2,
                      background: theme.palette.primary.main, color: 'white'
                    }} 
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 4 }}>
                {approvalHistory.length > 0 ? (
                  <Stack spacing={2}>
                    {(approvalRowsPerPage > 0
                      ? approvalHistory.slice(approvalPage * approvalRowsPerPage, approvalPage * approvalRowsPerPage + approvalRowsPerPage)
                      : approvalHistory
                    ).map((entry, index) => (
                      <Paper key={entry.id} sx={{ 
                        p: 3, 
                        background: entry.action_type === 'approved' 
                          ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)}, ${alpha(theme.palette.success.main, 0.02)})`
                          : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.05)}, ${alpha(theme.palette.error.main, 0.02)})`,
                        borderRadius: 2,
                        border: `1px solid ${entry.action_type === 'approved' ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.error.main, 0.2)}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`
                      }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label={entry.action_type === 'approved' ? 'APPROVED' : 'UNAPPROVED'}
                                size="small"
                                sx={{
                                  fontWeight: 500,
                                  borderRadius: 2,
                                  fontSize: '0.75rem',
                                  backgroundColor: entry.action_type === 'approved'
                                    ? alpha(theme.palette.success.main, 0.1)
                                    : alpha(theme.palette.error.main, 0.1),
                                  color: entry.action_type === 'approved' ? theme.palette.success.main : theme.palette.error.main,
                                  border: `1px solid ${entry.action_type === 'approved' ? theme.palette.success.main : theme.palette.error.main}`,
                                }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Signature
                            </Typography>
                            <Box sx={{ 
                              fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                              fontSize: '18px', 
                              color: entry.action_type === 'approved' ? theme.palette.success.main : theme.palette.error.main,
                              textShadow: '1px 1px 1px rgba(0,0,0,0.1)'
                            }}>
                              {entry.signature}
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Performed By
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {entry.profiles?.full_name 
                                ? entry.profiles.full_name
                                : entry.profiles?.first_name && entry.profiles?.last_name 
                                ? `${entry.profiles.first_name} ${entry.profiles.last_name}`
                                : entry.profiles?.first_name
                                ? entry.profiles.first_name
                                : entry.profiles?.last_name
                                ? entry.profiles.last_name
                                : entry.profiles?.email
                                ? entry.profiles.email.split('@')[0]
                                : entry.performed_by 
                                ? `User (${entry.performed_by.slice(0, 8)}...)`
                                : 'Unknown User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {entry.profiles?.email || 'No email available'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Date & Time
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatDateTime(entry.performed_at || entry.created_at)}
                            </Typography>
                          </Grid>
                          {entry.reason && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                                Reason
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                p: 2,
                                backgroundColor: alpha(theme.palette.grey[100], 0.5),
                                borderRadius: 1,
                                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                              }}>
                                {entry.reason}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <InfoIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.grey[600], mb: 1 }}>
                      No Approval History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This line production has not been approved or unapproved yet
                    </Typography>
                  </Box>
                )}
                
                {/* Pagination for Approval History */}
                {approvalHistory.length > 0 && (
                  <Box sx={{ 
                    borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    borderRadius: '0 0 12px 12px',
                    mt: 2
                  }}>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={approvalHistory.length}
                      rowsPerPage={approvalRowsPerPage}
                      page={approvalPage}
                      onPageChange={handleApprovalPageChange}
                      onRowsPerPageChange={handleApprovalRowsPerPageChange}
                      sx={{ 
                        px: 3,
                        py: 2,
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          color: theme.palette.text.primary
                        },
                        '& .MuiTablePagination-select': {
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          borderRadius: 2,
                          px: 2,
                          py: 1,
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          background: 'white',
                          '&:hover': {
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          }
                        },
                        '& .MuiTablePagination-actions': {
                          '& .MuiIconButton-root': {
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            borderRadius: 2,
                            margin: '0 2px',
                            background: 'white',
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              background: theme.palette.primary.main,
                              color: 'white',
                              transform: 'translateY(-1px)',
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                            },
                            '&.Mui-disabled': {
                              background: alpha(theme.palette.grey[300], 0.5),
                              color: theme.palette.grey[500],
                              border: `1px solid ${theme.palette.grey[300]}`,
                              transform: 'none',
                              boxShadow: 'none'
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>

          </Stack>
        </Grid>
      </Grid>
      
      {/* Approval Dialog */}
      <Dialog 
        open={approvalDialogOpen} 
        onClose={() => setApprovalDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.success.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 2.5, 
          borderBottom: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <VerifiedIcon sx={{ mr: 2 }} />
            Approve Line Production
          </Box>
          <IconButton 
            onClick={() => setApprovalDialogOpen(false)} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2, mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                  All required steps have been completed. This line production is ready for approval.
                </Typography>
                <Typography variant="body2">
                  This will mark the line production as completed and ready for final review. This action cannot be undone.
                </Typography>
              </Alert>
            </Grid>

            {/* Usage Transaction Option */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, border: `1px solid ${theme.palette.warning.main}`, borderRadius: 2, backgroundColor: alpha(theme.palette.warning.main, 0.05) }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={shouldCreateUsageTransactions}
                      onChange={(e) => setShouldCreateUsageTransactions(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: theme.palette.primary.main,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: theme.palette.primary.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        Create Usage Transactions & Deduct Inventory
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        Turn off if this line production was already completed and you're re-approving after unapproval
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Grid>

            {/* Supplies Usage Summary */}
            {summaryData.supplies && summaryData.supplies.length > 0 && shouldCreateUsageTransactions && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, border: `1px solid ${theme.palette.primary.main}`, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>
                    Supplies to be Deducted from Inventory:
                  </Typography>
                  <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    {summaryData.supplies.map((supply, index) => {
                      const supplyName = supply.supply_name || 'Unknown Supply';
                      const quantity = parseFloat(supply.actual_quantity) || 0;
                      const unit = supply.unit || 'units';
                      
                      if (quantity <= 0) return null;
                      
                      // Get lots based on item_type
                      const lots = supply.bottle_lots || supply.cap_lots || supply.label_lots || supply.supply_lots || [];
                      
                      return (
                        <Box key={index} sx={{ 
                          py: 1,
                          borderBottom: index < summaryData.supplies.length - 1 ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            mb: lots.length > 0 ? 0.5 : 0
                          }}>
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                              {supplyName} ({supply.item_type})
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                              -{quantity.toLocaleString('en-US', { maximumFractionDigits: 0 })} {unit} total
                            </Typography>
                          </Box>
                          {lots.length > 0 && (
                            <Box sx={{ pl: 2, mt: 0.5 }}>
                              {lots.map((lot, lotIndex) => {
                                const lotQuantity = parseFloat(lot.quantity_used) || 0;
                                const lotNumber = lot.lot_number || lot.batch_number || 'Unknown';
                                const batchNumber = lot.batch_number;
                                
                                if (lotQuantity <= 0) return null;
                                
                                return (
                                  <Box key={lotIndex} sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 0.25,
                                    fontSize: '0.75rem'
                                  }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      • Lot: {lotNumber}{batchNumber && batchNumber !== lotNumber ? `, Batch: ${batchNumber}` : ''}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'primary.main', ml: 2 }}>
                                      -{lotQuantity.toLocaleString('en-US', { maximumFractionDigits: 0 })} {lot.unit_used || unit}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      );
                    }).filter(Boolean)}
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                    These quantities will be recorded as usage transactions and deducted from inventory at the production location.
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Inventory Adjustment Option */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, border: `1px solid ${theme.palette.success.main}`, borderRadius: 2, backgroundColor: alpha(theme.palette.success.main, 0.05) }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={shouldCreateInventoryAdjustment}
                      onChange={(e) => setShouldCreateInventoryAdjustment(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: theme.palette.success.main,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: theme.palette.success.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        Create Add Stock Adjustment
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        Add the produced units to inventory with lot/batch tracking
                      </Typography>
                    </Box>
                  }
                />
                
                {shouldCreateInventoryAdjustment && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: 'success.main' }}>
                      Inventory Location:
                    </Typography>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleOpenInventoryLocationDialog}
                      sx={{
                        justifyContent: 'space-between',
                        textAlign: 'left',
                        height: 48,
                        borderRadius: 2,
                        borderColor: alpha(theme.palette.success.main, 0.5),
                        color: selectedInventoryLocation ? 'text.primary' : 'text.secondary',
                        fontWeight: 500,
                        px: 2,
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: theme.palette.success.main,
                          backgroundColor: alpha(theme.palette.success.main, 0.04)
                        }
                      }}
                      endIcon={<ExpandMoreIcon />}
                    >
                      {getSelectedInventoryLocationName()}
                    </Button>
                    
                    {/* Production Breakdown Display */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, color: 'success.main' }}>
                        Units to be Added:
                      </Typography>
                      <Grid container spacing={2}>
                        {lineProduction?.pallet_quantity > 0 && (
                          <Grid item xs={4}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 1.5, 
                              borderRadius: 2, 
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                            }}>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main' }}>
                                {lineProduction.pallet_quantity.toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Pallets
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {lineProduction?.case_quantity > 0 && (
                          <Grid item xs={4}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 1.5, 
                              borderRadius: 2, 
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                            }}>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main' }}>
                                {lineProduction.case_quantity.toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Cases
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {lineProduction?.unit_quantity > 0 && (
                          <Grid item xs={4}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 1.5, 
                              borderRadius: 2, 
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                            }}>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main' }}>
                                {lineProduction.unit_quantity.toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Individual Units
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                      <Typography variant="body2" sx={{ 
                        mt: 2, 
                        p: 1.5, 
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        fontWeight: 500, 
                        color: 'success.main',
                        textAlign: 'center'
                      }}>
                        Total: {(summaryData.yieldData?.total_units_produced || lineProduction?.planned_quantity || 0).toLocaleString()} units
                        {lineProduction?.batch_number && ` • Batch: ${lineProduction.batch_number}`}
                        {lineProduction?.lot_number && ` • Lot: ${lineProduction.lot_number}`}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Inventory Check Status */}
            {inventoryCheckLoading && shouldCreateUsageTransactions && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2">
                    Checking supplies inventory availability...
                  </Typography>
                </Alert>
              </Grid>
            )}
            
            {!inventoryCheckLoading && !hasEnoughInventory && insufficientInventoryItems.length > 0 && shouldCreateUsageTransactions && (
              <Grid item xs={12}>
                <Paper sx={{ 
                  p: 2, 
                  border: `1px solid ${theme.palette.error.main}`, 
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.error.main, 0.05)
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon sx={{ fontSize: '1.1rem' }} />
                    Insufficient Supplies Inventory
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
                    Cannot approve line production - the following supplies don't have enough inventory:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {insufficientInventoryItems.map((item, index) => (
                      <Box key={index} sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        py: 0.5,
                        borderBottom: index < insufficientInventoryItems.length - 1 ? `1px solid ${alpha(theme.palette.error.main, 0.2)}` : 'none'
                      }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ color: 'text.primary' }}>
                            {item.materialName}
                          </Typography>
                          {item.lotInfo && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                              {item.lotInfo}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                            Need: {item.requiredQuantity.toLocaleString('en-US', { maximumFractionDigits: 0 })} {item.unit}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Have: {item.availableQuantity.toLocaleString('en-US', { maximumFractionDigits: 0 })} {item.unit}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.dark' }}>
                            Short: {item.shortage.toLocaleString('en-US', { maximumFractionDigits: 0 })} {item.unit}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                    Please restock inventory or reduce supply quantities to proceed with approval.
                  </Typography>
                </Paper>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                Enter your name to confirm approval:
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Approver Name"
                fullWidth
                autoFocus
                required
                value={approvalSignature}
                onChange={(e) => {
                  setApprovalSignature(e.target.value);
                  setSignaturePreviewName(e.target.value);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 },
                    backgroundColor: 'white'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                mt: 2, 
                p: 2.5, 
                border: `1px solid ${theme.palette.grey[400]}`, 
                borderRadius: 2, 
                backgroundColor: alpha(theme.palette.grey[50], 0.8)
              }}>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                  Preview: Your signature will appear in script font
                </Typography>
                <Box sx={{ 
                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                  fontSize: '28px', 
                  color: theme.palette.grey[800],
                  mt: 2,
                  mb: 1,
                  textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {signaturePreviewName || 'Preview will appear here'}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2.5, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})`
        }}>
          <Button 
            onClick={() => setApprovalDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              '&:hover': {
                backgroundColor: alpha(theme.palette.success.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmApproval}
            variant="contained"
            disabled={approving || !approvalSignature.trim() || (shouldCreateUsageTransactions && (inventoryCheckLoading || !hasEnoughInventory)) || (shouldCreateInventoryAdjustment && !selectedInventoryLocation)}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}`
              },
              '&:disabled': {
                background: alpha(theme.palette.grey[400], 0.5),
                color: 'text.disabled'
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {approving ? 'Approving...' : 
             (shouldCreateUsageTransactions && inventoryCheckLoading) ? 'Checking Inventory...' :
             (shouldCreateUsageTransactions && !hasEnoughInventory) ? 'Insufficient Inventory' :
             (shouldCreateInventoryAdjustment && !selectedInventoryLocation) ? 'Select Inventory Location' :
             'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unapproval Dialog */}
      <Dialog 
        open={unapproveDialogOpen} 
        onClose={() => setUnapproveDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[300]}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 2.5, 
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ mr: 2, color: theme.palette.error.main }} />
            Unapprove Line Production
          </Box>
          <IconButton 
            onClick={() => setUnapproveDialogOpen(false)} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2, mt: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, color: '#000' }}>
              Are you sure you want to unapprove this line production?
            </Typography>
            <Typography variant="body2" sx={{ color: '#000' }}>
              This will mark the line production as pending and require re-approval. This action will be logged in the approval history.
            </Typography>
          </Alert>
          
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
            Reason for unapproval (required):
          </Typography>
          <TextField
            label="Reason"
            fullWidth
            multiline
            rows={3}
            required
            value={unapprovalReason}
            onChange={(e) => setUnapprovalReason(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 },
                backgroundColor: 'white'
              }
            }}
          />
          
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
            Enter your name to confirm unapproval:
          </Typography>
          <TextField
            label="Approver Name"
            fullWidth
            required
            value={unapprovalSignature}
            onChange={(e) => {
              setUnapprovalSignature(e.target.value);
              setUnapprovalSignaturePreview(e.target.value);
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 },
                backgroundColor: 'white'
              }
            }}
          />
          
          <Box sx={{ 
            mt: 2, 
            p: 2.5, 
            border: `1px solid ${theme.palette.grey[400]}`, 
            borderRadius: 2, 
            backgroundColor: 'white'
          }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
              Preview: Your signature will appear in script font
            </Typography>
            <Box sx={{ 
              fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
              fontSize: '28px', 
              color: 'theme.palette.grey[800]',
              mt: 2,
              mb: 1,
              textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center'
            }}>
              {unapprovalSignaturePreview || 'Preview will appear here'}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2.5, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`
        }}>
          <Button 
            onClick={() => setUnapproveDialogOpen(false)}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              color: theme.palette.grey[600],
              '&:hover': {
                backgroundColor: alpha(theme.palette.grey[200], 0.5)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmUnapproval} 
            variant="contained"
            disabled={unapproving || !unapprovalSignature.trim() || !unapprovalReason.trim()}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1,
              background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.error.dark}, ${theme.palette.error.main})`,
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}`
              },
              '&:disabled': {
                background: alpha(theme.palette.grey[400], 0.5),
                color: 'text.disabled'
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {unapproving ? 'Unapproving...' : 'Confirm Unapproval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inventory Location Selection Dialog */}
      <Dialog
        open={inventoryLocationDialogOpen}
        onClose={handleCloseInventoryLocationDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '40vh',
            width: '600px',
            maxWidth: '600px',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          fontSize: '1.25rem', 
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Select Inventory Location
          </Typography>
          <IconButton onClick={handleCloseInventoryLocationDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search locations..."
              value={inventoryLocationSearchTerm}
              onChange={(e) => setInventoryLocationSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredLocationsForInventory().map((location) => (
                  <TableRow 
                    key={location.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        pl: 3,
                        pr: 3
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        {location.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDialogInventoryLocationSelect(location)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          bgcolor: dialogSelectedInventoryLocation?.id === location.id ? 'black' : 'transparent',
                          color: dialogSelectedInventoryLocation?.id === location.id ? 'white' : 'inherit',
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            bgcolor: dialogSelectedInventoryLocation?.id === location.id ? 'black' : 'transparent',
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {getFilteredLocationsForInventory().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {inventoryLocationSearchTerm ? 'No locations found matching your search.' : 'No locations available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={() => window.open(`/company/${lineProduction?.company_id}/locations`, '_blank')}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              py: 1, 
              fontSize: '0.9rem', 
              fontWeight: 500,
              textTransform: 'none',
              border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
              color: 'success.main',
              '&:hover': {
                border: `1px solid ${theme.palette.success.main}`,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                transform: 'translateY(-1px)'
              }
            }}
          >
            Add Location
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleCloseInventoryLocationDialog}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[600]}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmInventoryLocationSelection}
              variant="contained"
              disabled={!dialogSelectedInventoryLocation}
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                },
                '&:disabled': {
                  background: alpha(theme.palette.grey[400], 0.5),
                  color: alpha(theme.palette.grey[600], 0.7)
                }
              }}
            >
              Confirm Selection
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      
    </Box>
  );
}